import { jsPDF } from 'jspdf';
import 'svg2pdf.js';

// svg2pdf v2 nie obsługuje fill="hsl(...)" / stroke="hsl(...)" — renderuje je jako transparentne.
// Konwertujemy do #RRGGBB przed eksportem.
const hslToHex = (h, s, l) => {
  s /= 100; l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const v = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * v).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const convertHslColor = (value) => {
  if (!value || typeof value !== 'string') return value;
  const m = value.match(/hsl\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i);
  if (!m) return value;
  return hslToHex(parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3]));
};

const normalizeHslColorsInSvg = (svg) => {
  let converted = 0;
  svg.querySelectorAll('*').forEach(el => {
    ['fill', 'stroke'].forEach(attr => {
      const v = el.getAttribute(attr);
      if (v && v.startsWith('hsl(')) {
        el.setAttribute(attr, convertHslColor(v));
        converted++;
      }
    });
  });
  return converted;
};

// Domyślny font jsPDF (Helvetica) używa WinAnsi i nie wspiera polskich znaków diakrytycznych
// (Ą, Ę, Ł, Ś, Ć, Ń, Ź, ż, ł itd. wychodzą jako krzaczki). Osadzamy Roboto z public/fonts/ — ma pełny Latin Extended.
// Cache base64 żeby fetch był jednorazowy (font ~170KB każdy).
const fontCache = { regular: null, bold: null };

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000; // unikamy stack overflow przy String.fromCharCode(...bytes)
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const fetchFontBase64 = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Nie udało się pobrać fontu: ${url} (HTTP ${response.status})`);
  return arrayBufferToBase64(await response.arrayBuffer());
};

const ensureRobotoLoaded = async () => {
  const base = process.env.PUBLIC_URL || '';
  if (!fontCache.regular) {
    fontCache.regular = await fetchFontBase64(`${base}/fonts/Roboto-Regular.ttf`);
  }
  if (!fontCache.bold) {
    fontCache.bold = await fetchFontBase64(`${base}/fonts/Roboto-Bold.ttf`);
  }
};

const registerRobotoInPdf = (pdf) => {
  pdf.addFileToVFS('Roboto-Regular.ttf', fontCache.regular);
  pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
  pdf.addFileToVFS('Roboto-Bold.ttf', fontCache.bold);
  pdf.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
  pdf.setFont('Roboto', 'normal');
};

// Wymuś Roboto na wszystkich <text> w klonie — svg2pdf czyta atrybut font-family
// i mapuje na font zarejestrowany w jsPDF. Brak atrybutu → Helvetica → krzaczki PL.
const forceRobotoOnSvgText = (svg) => {
  svg.querySelectorAll('text, tspan').forEach(el => {
    el.setAttribute('font-family', 'Roboto');
  });
};

/**
 * Eksportuje element SVG do pliku PDF.
 * @param {SVGElement} svgElement - element SVG do eksportu
 * @param {Object} [options] - opcje eksportu
 * @param {string} [options.filename='przekroj_geologiczny.pdf'] - nazwa pliku
 * @param {'landscape'|'portrait'} [options.orientation='landscape'] - orientacja strony
 * @param {'a4'|'a3'|'a2'|'a1'} [options.pageSize='a4'] - rozmiar strony
 * @param {number} [options.margin=10] - margines w mm
 * @param {{x:number,y:number,width:number,height:number}} [options.customViewBox] - wycinek SVG do eksportu (jeśli podany, eksportuje tylko ten prostokąt)
 */
export const exportSvgToPdf = async (svgElement, options = {}) => {
  const {
    filename = 'przekroj_geologiczny.pdf',
    orientation = 'landscape',
    pageSize = 'a4',
    margin = 10,
    customViewBox = null,
  } = options;

  if (!svgElement) {
    throw new Error('Brak elementu SVG do eksportu');
  }

  // Pełne wymiary SVG (potrzebne gdy customViewBox nie jest podany)
  const fullSvgWidth = svgElement.width?.baseVal?.value || svgElement.getBoundingClientRect().width;
  const fullSvgHeight = svgElement.height?.baseVal?.value || svgElement.getBoundingClientRect().height;

  if (!fullSvgWidth || !fullSvgHeight) {
    throw new Error('Nie można odczytać wymiarów SVG');
  }

  // Wymiary użyte do renderowania w PDF — z customViewBox jeśli podany, inaczej pełne
  const svgWidth = customViewBox ? customViewBox.width : fullSvgWidth;
  const svgHeight = customViewBox ? customViewBox.height : fullSvgHeight;

  // Klonuj SVG żeby nie modyfikować oryginału
  const clonedSvg = svgElement.cloneNode(true);

  // Usuń overlay UI (prostokąt zaznaczenia wydruku, podpowiedzi snap, podgląd rysowania itp.)
  // — wszystkie elementy oznaczone data-print-overlay nie powinny trafić do PDF.
  clonedSvg.querySelectorAll('[data-print-overlay="true"]').forEach(el => el.remove());

  // svg2pdf v2 nie wspiera hsl() — konwersja na #hex.
  const hslConverted = normalizeHslColorsInSvg(clonedSvg);
  if (hslConverted > 0) console.log('[pdfExport] Skonwertowano kolorów hsl→hex:', hslConverted);

  // Wymuszamy Roboto na wszystkich <text>/<tspan> żeby svg2pdf użył naszego osadzonego fontu (PL znaki).
  forceRobotoOnSvgText(clonedSvg);

  // Załaduj font do cache (jednorazowo) — robimy to przed utworzeniem pdf żeby błąd fetcha
  // rzucił się przed dodaniem klonu do DOM.
  try {
    await ensureRobotoLoaded();
  } catch (err) {
    console.warn('[pdfExport] Nie udało się załadować Roboto, polskie znaki mogą być uszkodzone:', err.message);
  }

  // svg2pdf.js wymaga atrybutów width, height i viewBox jako wartości numerycznych.
  // viewBox kontroluje jaki wycinek SVG zostanie wyrenderowany — customViewBox tym kanałem przycina obszar do druku.
  clonedSvg.setAttribute('width', svgWidth);
  clonedSvg.setAttribute('height', svgHeight);
  if (customViewBox) {
    clonedSvg.setAttribute('viewBox', `${customViewBox.x} ${customViewBox.y} ${customViewBox.width} ${customViewBox.height}`);
  } else if (!clonedSvg.getAttribute('viewBox')) {
    clonedSvg.setAttribute('viewBox', `0 0 ${fullSvgWidth} ${fullSvgHeight}`);
  }

  // Diagnostyka — co finalnie idzie do svg2pdf
  const counts = {};
  clonedSvg.querySelectorAll('*').forEach(el => {
    counts[el.tagName] = (counts[el.tagName] || 0) + 1;
  });
  console.log('[pdfExport] Elementy w klonie:', counts);
  console.log('[pdfExport] viewBox:', clonedSvg.getAttribute('viewBox'));
  console.log('[pdfExport] size:', svgWidth, 'x', svgHeight);
  const samplePaths = Array.from(clonedSvg.querySelectorAll('path')).slice(0, 5).map(p => ({
    fill: p.getAttribute('fill'),
    opacity: p.getAttribute('opacity'),
    d: (p.getAttribute('d') || '').slice(0, 80),
  }));
  console.log('[pdfExport] Pierwsze 5 pathów:', samplePaths);

  // Dodaj do DOM tymczasowo (svg2pdf potrzebuje elementu w dokumencie)
  clonedSvg.style.position = 'absolute';
  clonedSvg.style.left = '-99999px';
  clonedSvg.style.top = '-99999px';
  document.body.appendChild(clonedSvg);

  try {
    // Utwórz PDF
    const pdf = new jsPDF({
      orientation,
      unit: 'mm',
      format: pageSize,
    });

    // Zarejestruj Roboto w jsPDF (jeśli font się załadował) — svg2pdf użyje go dla każdego <text font-family="Roboto">.
    if (fontCache.regular && fontCache.bold) {
      registerRobotoInPdf(pdf);
    }

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Obszar dostępny po odjęciu marginesów
    const availableWidth = pageWidth - 2 * margin;
    const availableHeight = pageHeight - 2 * margin;

    let pdfWidth, pdfHeight;
    if (customViewBox) {
      // Eksport wycinka: SVG ma już zakodowaną skalę 1:N w px/m (px/m = 1000 × 96/25.4 / N).
      // Mapowanie 1 CSS px → 1/3.7795 mm zachowuje fizyczną skalę 1:N na papierze.
      // Fit-to-page jest świadomie pominięty — jeśli rect mieści się w (paper − 2×margin), wyjdzie 1:N idealnie.
      const CSS_PX_PER_MM = 96 / 25.4;
      pdfWidth = svgWidth / CSS_PX_PER_MM;
      pdfHeight = svgHeight / CSS_PX_PER_MM;
      if (pdfWidth > availableWidth + 0.5 || pdfHeight > availableHeight + 0.5) {
        console.warn(
          `[pdfExport] customViewBox (${pdfWidth.toFixed(1)}×${pdfHeight.toFixed(1)} mm) ` +
          `przekracza obszar drukowalny (${availableWidth}×${availableHeight} mm) — wydruk zostanie obcięty. ` +
          `Zmniejsz prostokąt wydruku lub margines.`
        );
      }
    } else {
      // Eksport całego SVG bez wycinka — fit-to-page (skala 1:N nie ma tu zastosowania).
      const s = Math.min(availableWidth / svgWidth, availableHeight / svgHeight);
      pdfWidth = svgWidth * s;
      pdfHeight = svgHeight * s;
    }

    // Centruj na stronie
    const offsetX = margin + (availableWidth - pdfWidth) / 2;
    const offsetY = margin + (availableHeight - pdfHeight) / 2;

    // Renderuj SVG do PDF
    await pdf.svg(clonedSvg, {
      x: offsetX,
      y: offsetY,
      width: pdfWidth,
      height: pdfHeight,
    });

    // Zapisz plik
    pdf.save(filename);
  } finally {
    // Usuń tymczasowy klon z DOM
    document.body.removeChild(clonedSvg);
  }
};
